import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCart, updateItem, removeItem } from '../../store/slices/cartSlice';
import Spinner from '../../components/common/Spinner';
import imgUrl from '../../utils/imgUrl';
import useIsMobile from '../../hooks/useIsMobile';

export default function Cart() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { items, subtotal, loading } = useSelector((s) => s.cart);
  const isMobile  = useIsMobile();

  useEffect(() => { dispatch(fetchCart()); }, [dispatch]);

  const shipping = parseFloat(subtotal) > 999 ? 0 : 99;
  const total    = (parseFloat(subtotal) + shipping).toFixed(2);

  if (loading) return <Spinner />;

  return (
    <div className="page-top">
      {/* Header */}
      <div style={{ ...S.header, padding: isMobile ? '32px 18px' : '48px 56px' }}>
        <p className="sec-label">Review</p>
        <h1 style={S.title}>YOUR <span style={{ color: 'var(--gold)' }}>CART</span></h1>
        {items.length > 0 && <p style={S.count}>{items.length} item{items.length !== 1 ? 's' : ''}</p>}
      </div>

      {items.length === 0 ? (
        <div style={S.empty}>
          <p style={{ fontSize: '64px', marginBottom: '16px' }}>🛒</p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', letterSpacing: '2px', marginBottom: '12px' }}>YOUR CART IS EMPTY</h2>
          <p style={{ color: '#888', marginBottom: '28px' }}>Looks like you haven't added anything yet.</p>
          <Link to="/products" className="btn-primary">Start Shopping →</Link>
        </div>
      ) : (
        <div style={{ ...S.wrap, gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', padding: isMobile ? '20px 18px' : '48px 56px', gap: isMobile ? '20px' : '40px' }}>
          {/* Items */}
          <div style={S.itemsCol}>
            {!isMobile && (
              <div style={S.tableHead}>
                <span style={{ flex: 2 }}>Product</span>
                <span style={{ textAlign: 'center' }}>Qty</span>
                <span style={{ textAlign: 'right' }}>Total</span>
                <span style={{ width: '30px' }} />
              </div>
            )}
            {items.map((item) => (
              <div key={item.id} style={{ ...S.row, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '12px' : '20px' }}>
                <div style={{ flex: 2, display: 'flex', gap: '16px', alignItems: 'center', minWidth: 0 }}>
                  <div style={S.itemImg}>
                    {item.image
                      ? <img src={imgUrl(item.image)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: '28px' }}>👕</span>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={S.itemName}>{item.name}</p>
                    <p style={S.itemPrice}>₹{item.price} each</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '20px', marginLeft: isMobile ? 'auto' : 0 }}>
                  <div style={S.qtyWrap}>
                    <button style={S.qtyBtn} onClick={() => dispatch(updateItem({ id: item.id, quantity: item.quantity - 1 }))}>−</button>
                    <span style={S.qtyVal}>{item.quantity}</span>
                    <button style={S.qtyBtn} onClick={() => dispatch(updateItem({ id: item.id, quantity: item.quantity + 1 }))}>+</button>
                  </div>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: '18px', letterSpacing: '1px', minWidth: '72px', textAlign: 'right' }}>₹{(item.price * item.quantity).toFixed(2)}</p>
                  <button style={S.removeBtn} onClick={() => dispatch(removeItem(item.id))}>✕</button>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div style={{ ...S.summary, position: isMobile ? 'relative' : 'sticky' }}>
            <p style={S.summaryTitle}>ORDER SUMMARY</p>
            <div style={S.summaryRow}><span>Subtotal</span><span>₹{subtotal}</span></div>
            <div style={S.summaryRow}>
              <span>Shipping</span>
              <span style={{ color: shipping === 0 ? 'var(--green)' : 'inherit' }}>
                {shipping === 0 ? 'FREE' : `₹${shipping}`}
              </span>
            </div>
            {shipping > 0 && (
              <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '12px' }}>
                Add ₹{(999 - parseFloat(subtotal)).toFixed(0)} more for free shipping
              </p>
            )}
            <div style={S.divider} />
            <div style={{ ...S.summaryRow, fontFamily: 'var(--font-display)', fontSize: '22px', letterSpacing: '1px' }}>
              <span>TOTAL</span><span>₹{total}</span>
            </div>
            <button className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '16px', marginBottom: '10px' }}
              onClick={() => navigate('/checkout')}>
              PROCEED TO CHECKOUT →
            </button>
            <Link to="/products" style={{ display: 'block', textAlign: 'center', fontSize: '12px', color: '#888', letterSpacing: '1px', textTransform: 'uppercase' }}>
              ← Continue Shopping
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  header:     { background: 'var(--black)', padding: '48px 56px', borderBottom: '3px solid var(--gold)' },
  title:      { fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,56px)', letterSpacing: '2px', color: '#fff', lineHeight: 1 },
  count:      { color: 'rgba(255,255,255,.4)', fontSize: '13px', marginTop: '8px' },
  empty:      { textAlign: 'center', padding: '80px 40px' },
  wrap:       { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '40px', padding: '48px 56px', maxWidth: '1300px', margin: '0 auto' },
  itemsCol:   { background: '#fff', border: '1px solid var(--border)' },
  tableHead:  { display: 'flex', alignItems: 'center', gap: '20px', padding: '12px 20px', borderBottom: '2px solid var(--border)', fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#888' },
  row:        { display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  itemImg:    { width: '72px', height: '90px', background: 'var(--light)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  itemName:   { fontWeight: 600, fontSize: '14px', marginBottom: '4px' },
  itemPrice:  { fontSize: '12px', color: '#888' },
  qtyWrap:    { display: 'inline-flex', border: '1.5px solid var(--border)', textAlign: 'center' },
  qtyBtn:     { width: '30px', height: '30px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' },
  qtyVal:     { width: '36px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', fontWeight: 700, fontSize: '14px' },
  removeBtn:  { background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '14px', width: '30px', padding: 0 },
  summary:    { background: '#fff', border: '1px solid var(--border)', padding: '24px', height: 'fit-content', position: 'sticky', top: '120px' },
  summaryTitle:{ fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '3px', marginBottom: '20px', borderBottom: '2px solid var(--red)', paddingBottom: '10px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '10px' },
  divider:    { height: '1px', background: 'var(--border)', margin: '14px 0' },
};
